import { useLocation } from "react-router-dom";
import usePageLanguage from "./usePageLanguage.js";
import { GUIDES_BASE_PATH } from "../content/guides/guideModel.js";
import { findGuideEntry } from "../content/guides/registry.js";
import { stripLanguagePrefix } from "../lib/language.js";

// Semantic page names for analytics, keyed by route path. The catch-all route
// (`*`) renders Home, so any unmapped path is reported as "home" too. Keeping
// the mapping here (not scattered across call sites) means the same click on a
// shared component — navbar, footer — reports the page it actually rendered on.
const PAGE_BY_PATH = {
  "/": "home",
  "/home": "home",
  "/wedding-photo-sharing": "wedding_photo_sharing",
  "/digital-wedding-invitation": "digital_wedding_invitation",
  "/wedding-planning": "wedding_planning",
  "/wedding-rsvp": "wedding_rsvp",
  "/wedding-seating": "wedding_seating",
  // The guides index. `/guides/<slug>` is handled by `guidePageName` below;
  // this is the bare path, which that branch does not match.
  "/guides": "guides",
  "/contact": "contact",
  "/terms": "terms",
  "/privacy-policy": "privacy",
  "/cookie-policy": "cookies",
};

/**
 * Resolve the shared CTA context (page + language) for analytics call sites.
 * The analytics module itself is framework-agnostic (no router, no i18n) by
 * design — this hook is the landing-page adapter that feeds it the values it
 * can't derive on its own. Spread the result into `trackCTA` / `trackLead`
 * alongside a per-CTA `ctaName` (and `plan` where applicable):
 *
 *   const analytics = useAnalyticsContext();
 *   trackCTA("navbar", { ...analytics, ctaName: "register" });
 *
 * @returns {{ page: string, language: string }}
 */
/**
 * Guides share one route (`/guides/:slug`), so they cannot be listed in
 * `PAGE_BY_PATH` without adding a line per guide and forgetting one. The name
 * is derived from the slug instead — `/guides/wedding-seating-plan` reports as
 * `guide_wedding_seating_plan` — which keeps per-guide attribution without a
 * second list to maintain.
 *
 * The slug in the URL is resolved back to the guide's IDENTITY first, because a
 * translated guide has its own slug: `/el/guides/plano-trapezion-gamou` is the
 * Greek version of `guide_wedding_seating_plan`, not a different page. Naming
 * it from the URL alone would split one guide into two page names, and every
 * comparison between the English and Greek versions of it would silently
 * measure two things instead of one language dimension over one thing.
 */
const guidePageName = (pathname) => {
  const slug = pathname.slice(`${GUIDES_BASE_PATH}/`.length).replace(/\/$/, "");
  if (!slug) return "home";
  const identity = findGuideEntry(slug)?.slug ?? slug;
  return `guide_${identity.replace(/-/g, "_")}`;
};

export default function useAnalyticsContext() {
  const { pathname } = useLocation();

  // A `/el` path reports Greek on the strength of the URL alone; an unprefixed
  // path defers to the language actually rendered. The rule lives in
  // `usePageLanguage`, so the head, the switcher and this event cannot end up
  // disagreeing about what language a page is in.
  const language = usePageLanguage();

  // The language prefix is stripped before the page is named, so a Greek page
  // reports the page it actually is. Without this every `/el/...` path misses
  // `PAGE_BY_PATH`, falls through the `|| "home"` default, and every CTA on
  // the Greek site is attributed to the homepage — silently, because the event
  // still fires and still looks well-formed.
  const basePath = stripLanguagePrefix(pathname);

  const page = basePath.startsWith(`${GUIDES_BASE_PATH}/`)
    ? guidePageName(basePath)
    : PAGE_BY_PATH[basePath] || "home";

  return { page, language };
}
